import rules, { roleRules } from './rbac-rules';

/**
 * Check if a user has access to a specific action
 * @param {string} license - User's license
 * @param {string} action - Action to check (e.g., 'label-management-page:visit')
 * @param {Object} data - Additional data for dynamic permissions
 * @param {string|null} role - User's role (defaults to 'basic' if not provided)
 * @returns {boolean} True if access is allowed, false if denied
 */
const check = (license, action, data, role = null) => {
  // Default role for users who don't have one set
  const effectiveRole = role || 'basic';

  // First check role-based restrictions (these take precedence)
  if (effectiveRole && roleRules[effectiveRole]) {
    const roleRestrictions = roleRules[effectiveRole];
    if (roleRestrictions.denied && roleRestrictions.denied.includes(action)) {
      // User's role explicitly denies this action
      return false;
    }
  }

  // Then check license-based permissions
  const permissions = rules[license];
  if (!permissions) {
    // role is not present in the rules
    return false;
  }

  const staticPermissions = permissions.static;

  if (staticPermissions && staticPermissions.includes(action)) {
    // static rule not provided for action
    return true;
  }

  const dynamicPermissions = permissions.dynamic;

  if (dynamicPermissions) {
    const permissionCondition = dynamicPermissions[action];
    if (!permissionCondition) {
      // dynamic rule not provided for action
      return false;
    }

    return permissionCondition(data);
  }
  return false;
};

/**
 * Check if a user can access a specific action based on their role only
 * @param {string|null} role - User's role
 * @param {string} action - Action to check
 * @returns {boolean} True if role allows it, false if role denies it
 */
const checkRoleAccess = (role, action) => {
  const effectiveRole = role || 'basic';
  const roleRestrictions = roleRules[effectiveRole];

  if (!roleRestrictions || !roleRestrictions.denied) {
    return true; // No restrictions for this role
  }

  return !roleRestrictions.denied.includes(action);
};

/**
 * Get the effective role for a user (with default fallback)
 * @param {string|null} role - User's explicit role
 * @returns {string} The effective role to use
 */
const getEffectiveRole = (role) => role || 'basic';

/**
 * Check if a specific role exists in the system
 * @param {string} role - Role to check
 * @returns {boolean} True if role exists
 */
const roleExists = (role) => role in roleRules;

function Can({ license, perform, data, yes = () => null, no = () => null, role = null }) {
  const result = check(license, perform, data, role);

  return result ? yes() : no();
}

export default Can;
export { check, checkRoleAccess, getEffectiveRole, roleExists };
